import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from 'recharts';
import { ChartSeries } from '../../types/insights';
import { useShelterState } from '../../hooks/useShelter';

interface EmptyMonthlyAdoptionsChartProps {
  series: ChartSeries[];
  height?: number;
  className?: string;
  hasCompleteData?: boolean;
  isPetszelManaged?: boolean;
}

interface EmptyStateLabelProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  hasCompleteData: boolean;
  isPetszelManaged?: boolean;
}

const CHART_COLORS = {
  labelFill: '#003672',
  axisText: 'rgba(0, 0, 0, 0.3)',
  gridStroke: 'rgba(0, 0, 0, 0.2)',
  hatchStroke: '#000000',
  hatchFill: '#fff',
};

const CHART_CONSTANTS = {
  barSize: 176,
  barValue: 2, // Approx 24px height on 314px chart
  fontSize: 20,
  tickFontSize: 14,
  fontWeight: 700,
  tickFontWeight: 500,
};

const EmptyStateLabel: React.FC<EmptyStateLabelProps> = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  hasCompleteData,
  isPetszelManaged,
}) => {
  const { shelter } = useShelterState();

  // Determine whether to show '0' or '--'
  const getDisplayValue = (): string => {
    if (hasCompleteData) {
      return '0';
    }
    // If shelter is not petszel-enabled, show '--'
    if (!shelter?.is_petszel_enabled) {
      return '--';
    }
    // For petszel-enabled shelters, use the passed flag
    if (isPetszelManaged) {
      return '0';
    }
    // Pre-cutoff month for petszel-enabled shelter
    return '--';
  };

  return (
  <text
    x={x + width / 2}
    y={y + height / 2 + 8}
    fill={CHART_COLORS.labelFill}
    textAnchor="middle"
    fontSize={CHART_CONSTANTS.fontSize}
    fontWeight={CHART_CONSTANTS.fontWeight}
  >
    {getDisplayValue()}
  </text>
  );
};

export const EmptyMonthlyAdoptionsChart: React.FC<EmptyMonthlyAdoptionsChartProps> = ({
  series,
  height = 314,
  className = '',
  hasCompleteData = true,
  isPetszelManaged,
}) => {
  const emptyStateData = useMemo(
    () =>
      series.map((s) => ({
        name: s.label,
        value: CHART_CONSTANTS.barValue,
        displayValue: 0,
        key: s.key,
      })),
    [series]
  );

  // Custom label renderer that passes the petszel-managed flag for each bar
  const renderCustomLabel = (props: any) => {
    const { x, y, width, height } = props;
    return (
      <EmptyStateLabel
        x={x}
        y={y}
        width={width}
        height={height}
        hasCompleteData={hasCompleteData}
        isPetszelManaged={isPetszelManaged}
      />
    );
  };

  return (
    <div className={className} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={emptyStateData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          barCategoryGap="15%"
        >
          <defs>
            <pattern
              id="diagonalHatch"
              patternUnits="userSpaceOnUse"
              width="8"
              height="8"
              patternTransform="rotate(45)"
            >
              <rect width="8" height="8" fill={CHART_COLORS.hatchFill} opacity={0} />
              <path
                d="M0,4 h8"
                stroke={CHART_COLORS.hatchStroke}
                strokeOpacity={0.2}
                strokeWidth={1}
              />
            </pattern>
          </defs>
          <CartesianGrid
            strokeDasharray="none"
            stroke={CHART_COLORS.gridStroke}
            horizontal={true}
            vertical={false}
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: CHART_CONSTANTS.tickFontSize,
              fontWeight: CHART_CONSTANTS.tickFontWeight,
              fill: CHART_COLORS.axisText,
            }}
            interval={0}
          />
          <YAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: CHART_CONSTANTS.tickFontSize,
              fontWeight: CHART_CONSTANTS.tickFontWeight,
              fill: CHART_COLORS.axisText,
            }}
            domain={[0, 20]}
            ticks={[0, 5, 10, 15, 20]}
            allowDecimals={false}
          />
          <Tooltip cursor={false} content={() => null} />
          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            label={renderCustomLabel}
            isAnimationActive={false}
            barSize={CHART_CONSTANTS.barSize}
          >
            {emptyStateData.map((_, index) => (
              <Cell key={`cell-${index}`} fill="url(#diagonalHatch)" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
